import { displayModelName } from "@/data/modelPages";
/**
 * SSR metadata for listing detail pages.
 *
 * The route loader fetches a small, serializable slice of the listing so the
 * server HTML carries a real title, description, OG image and Vehicle JSON-LD
 * instead of a generic slug-derived title.
 */
import { redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { buildListingSlug, extractListingId } from "@/utils/listingSlug";

export function canonicalListingSlug(listing: {
  id: string; make: string | null; model: string | null; year: number | null; location_city: string | null;
}): string {
  return buildListingSlug(listing);
}

/**
 * Loader for listing detail routes: loads SEO data and issues a 301 to the
 * canonical slug when the requested slug differs (prevents duplicate URLs).
 */
export async function loadListingWithRedirect(slug: string | undefined, locale: "ro" | "uk" | "de") {
  const data = await loadListingSeo(slug);
  if (data) {
    const canonical = canonicalListingSlug(data);
    if (canonical && slug !== canonical) {
      const prefix = locale === "ro" ? "/listing" : `/${locale}/listing`;
      throw redirect({ href: `${prefix}/${canonical}`, statusCode: 301, replace: true });
    }
  }
  return data;
}

const SITE = "RO Auto Market";
const ORIGIN = "https://roautomarket.ro";

export type ListingSeoData = {
  id: string;
  make: string | null;
  model: string | null;
  year: number | null;
  price: number | null;
  currency: string | null;
  mileage_value: number | null;
  location_city: string | null;
  location_country: string | null;
  description_text: string | null;
  condition: string | null;
  photo: string | null;
  /** Full listing row, so the detail page renders server-side. */
  row: Record<string, unknown> | null;
};

export type ListingLocale = "ro" | "uk" | "de";

const PREFIX: Record<ListingLocale, string> = {
  ro: "/listing",
  uk: "/uk/listing",
  de: "/de/listing",
};

export async function loadListingSeo(slug: string | undefined): Promise<ListingSeoData | null> {
  const id = extractListingId(slug);
  if (!id) return null;

  const { data, error } = await supabase
    .from("listings_v2")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error || !data) return null;

  const photos = (data as { photos?: string[] | null }).photos;
  return {
    id: data.id,
    make: data.make ?? null,
    model: data.model ?? null,
    year: data.year ?? null,
    price: data.price ?? null,
    currency: data.currency ?? null,
    mileage_value: data.mileage_value ?? null,
    location_city: data.location_city ?? null,
    location_country: data.location_country ?? null,
    description_text: data.description_text ?? null,
    condition: data.condition ?? null,
    photo: Array.isArray(photos) && photos.length > 0 ? photos[0] : null,
    row: data as Record<string, unknown>,
  };
}


function formatPrice(price: number | null, currency: string | null): string {
  if (!price) return "";
  const symbol = currency === "GBP" ? "£" : currency === "RON" ? " RON" : " €";
  const value = new Intl.NumberFormat("ro-RO").format(price);
  return currency === "GBP" ? `${symbol}${value}` : `${value}${symbol}`;
}

export function boundedListingTitle(name: string, price: string, locale: ListingLocale = 'ro') {
  const suffix = locale === 'de' ? 'kaufen' : locale === 'uk' ? 'for sale' : 'de vânzare';
  const tail = price ? ` | ${price}` : '';
  const candidates = [`${name} ${suffix}${tail} | ${SITE}`, `${name}${tail} | ${SITE}`, `${name}${tail}`];
  const short = candidates.find((candidate) => candidate.length < 60);
  if (short) return short;
  const budget = Math.max(1, 59 - tail.length - 1);
  return `${name.slice(0, budget).trimEnd()}…${tail}`;
}

export function listingHead(
  listing: ListingSeoData | null,
  slug: string | undefined,
  locale: ListingLocale = "ro",
) {
  // Canonical always uses the slug built from listing data, never the requested URL.
  if (listing) slug = canonicalListingSlug(listing);
  const canonical = `${ORIGIN}${PREFIX[locale]}/${slug ?? ""}`;

  if (!listing) {
    const fallback = `Anunț auto | ${SITE}`;
    return {
      meta: [
        { title: fallback },
        { name: "description", content: "Detalii anunț auto pe RO Auto Market." },
        { name: "robots", content: locale === "ro" ? "noindex, follow" : "noindex, nofollow" },
      ],
      links: [],
    };
  }

  const name = [listing.make, displayModelName({ make: listing.make, model: listing.model, external_id: (listing.row?.external_id as string) ?? null, description_text: listing.description_text }), listing.year].filter(Boolean).join(" ").trim();
  const price = formatPrice(listing.price, listing.currency);
  const bits = [
    price,
    listing.mileage_value ? `${new Intl.NumberFormat("ro-RO").format(listing.mileage_value)} km` : "",
    listing.location_city ?? "",
  ].filter(Boolean);

  const title = boundedListingTitle(name, price, locale);

  const rawDescription = (listing.description_text ?? "").replace(/\s+/g, " ").trim();
  const description = (
    rawDescription.length > 60
      ? `${name}${bits.length ? `: ${bits.join(", ")}` : ""}. ${rawDescription}`
      : `${name}${bits.length ? `: ${bits.join(", ")}` : ""}. Vezi fotografii, dotări și contactează vânzătorul direct pe ${SITE}.`
  ).slice(0, 158);

  const meta: Array<Record<string, string>> = [
    { title },
    { name: "title", content: title },
    { name: "description", content: description },
    { property: "og:type", content: "product" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: canonical },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
  ];

  if (locale !== "ro") meta.push({ name: "robots", content: "noindex, nofollow" });

  if (listing.photo?.startsWith("https://")) {
    meta.push({ property: "og:image", content: listing.photo });
    meta.push({ name: "twitter:image", content: listing.photo });
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": ["Product", "Car"],
    name,
    url: canonical,
    ...(listing.make ? { brand: { "@type": "Brand", name: listing.make } } : {}),
    ...(listing.model ? { model: listing.model } : {}),
    ...(listing.year ? { productionDate: String(listing.year) } : {}),
    ...(listing.photo ? { image: listing.photo } : {}),
    ...(typeof listing.row?.vin === "string" && listing.row.vin
      ? { vehicleIdentificationNumber: listing.row.vin }
      : {}),
    ...(typeof listing.mileage_value === 'number' && Number.isFinite(listing.mileage_value) && listing.mileage_value >= 0
      ? {
          mileageFromOdometer: {
            "@type": "QuantitativeValue",
            value: listing.mileage_value,
            unitCode: listing.row?.mileage_unit === 'mi' || listing.row?.mileage_unit === 'miles' ? 'SMI' : 'KMT',
          },
        }
      : {}),
    ...(() => {
      const attrs = listing.row?.attributes as Record<string, unknown> | null | undefined;
      const fuel = attrs?.fuel_type ?? attrs?.fuel ?? listing.row?.fuel_type ?? listing.row?.fuel;
      return typeof fuel === 'string' && fuel.trim() ? { fuelType: fuel.trim() } : {};
    })(),
    ...(listing.price
      ? {
          offers: {
            "@type": "Offer",
            price: listing.price,
            priceCurrency: listing.currency ?? "EUR",
            availability: "https://schema.org/InStock",
            url: canonical,
            itemCondition:
              listing.condition === "new"
                ? "https://schema.org/NewCondition"
                : "https://schema.org/UsedCondition",
          },
        }
      : {}),
  };

  // Only the Romanian listing is indexable until other markets have inventory.
  const alternates = [
    { rel: "alternate", hrefLang: "ro-RO", href: `${ORIGIN}${PREFIX.ro}/${slug ?? ""}` },
    { rel: "alternate", hrefLang: "x-default", href: `${ORIGIN}${PREFIX.ro}/${slug ?? ""}` },
  ];

  return {
    meta,
    links: [{ rel: "canonical", href: canonical }, ...alternates],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }],
  };
}

